/**
 * Support — AquaStar customer self-service issue tracker (Phase 7).
 *
 * Route: /support (authed). Lets a customer report a problem and follow
 * their own issues. Talks to the shared Nexus Issue Tracker over the
 * app-facing REST routes (/v1/app/aquastar/issues/*) via appRest(). The
 * customer's bearer JWT marks them as a CLIENT server-side, so they only
 * ever see their own issues and the public projection — internal staff
 * notes and the dev task breakdown never reach this page.
 *
 * Two views:
 *   - list  : "My issues" + a "Report a problem" button
 *   - report: a meta-driven form (type/severity from /issues/meta)
 *   - detail: status + the public timeline, with a public reply box
 */

import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppHeader } from '../components/AppHeader';
import { ApiError, appRest } from '../lib/apiClient';

interface MetaType { id: string; key: string; name: string; description: string | null; workClass?: string }
interface MetaSeverity { id: string; key: string; name: string; rank: number }
interface MetaStatus { id: string; key: string; name: string; statusCategory: string }
interface Meta { types: MetaType[]; severities: MetaSeverity[]; statuses: MetaStatus[] }

interface Issue {
  id: string; key: string; title: string; description: string | null;
  typeId: string; severityId: string; statusId: string; createdAt: string;
}
interface Comment { id: string; body: string; authorKind: string; visibility: string; createdAt: string }
interface PageResp { items: Issue[]; total: number }
interface DetailResp { issue: Issue; comments: Comment[] }

export function Support() {
  const { issueId } = useParams();
  if (issueId) return <SupportDetail issueId={issueId} />;
  return <SupportList />;
}

// ── list + report ─────────────────────────────────────────────────────

function SupportList() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState<Meta | null>(null);
  const [rows, setRows] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [reporting, setReporting] = useState(false);

  const statusById = useMemo(() => new Map((meta?.statuses ?? []).map((s) => [s.id, s])), [meta]);
  const typeById = useMemo(() => new Map((meta?.types ?? []).map((t) => [t.id, t])), [meta]);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [m, list] = await Promise.all([
        appRest<Meta>('GET', '/issues/meta'),
        appRest<PageResp>('GET', '/issues?limit=100'),
      ]);
      setMeta(m); setRows(list.items ?? []);
    } catch (e) { setErr(fmt(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  return (
    <>
      <AppHeader />
      <div className="container py-4" style={{ maxWidth: 820 }}>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h2 className="mb-1">Support</h2>
            <div className="text-muted small">Report a problem with your pool system and track our response.</div>
          </div>
          {!reporting && <button className="btn btn-primary" onClick={() => setReporting(true)}>Report a problem</button>}
        </div>

        {err ? <div className="alert alert-warning">{err}</div> : null}

        {reporting && meta ? (
          <ReportForm meta={meta} onCancel={() => setReporting(false)} onCreated={(id) => navigate(`/support/${id}`)} onError={setErr} />
        ) : null}

        <h6 className="text-muted text-uppercase small mt-4">My issues</h6>
        {loading ? (
          <div className="text-muted">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-muted">You haven’t reported any issues yet.</div>
        ) : (
          <div className="list-group">
            {rows.map((r) => (
              <button key={r.id} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center" onClick={() => navigate(`/support/${r.id}`)}>
                <span>
                  <span className="fw-semibold">{r.title}</span>
                  <span className="text-muted small ms-2">{typeById.get(r.typeId)?.name ?? ''}</span>
                </span>
                <span><StatusBadge name={statusById.get(r.statusId)?.name} cat={statusById.get(r.statusId)?.statusCategory} /> <span className="text-muted small ms-2">{rel(r.createdAt)}</span></span>
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function ReportForm({ meta, onCancel, onCreated, onError }: {
  meta: Meta; onCancel: () => void; onCreated: (id: string) => void; onError: (m: string) => void;
}) {
  // Customers only see customer-facing types (work_class 'client_issue').
  // App defects, feature requests, and enhancements are internal/dev work and
  // stay out of the customer form. The backend's meta endpoint already filters
  // these out for client callers; this is a defensive guard for any meta that
  // arrives unfiltered (and falls back to all types if work_class is absent).
  const reportableTypes = meta.types.some((t) => t.workClass)
    ? meta.types.filter((t) => t.workClass === 'client_issue')
    : meta.types;
  const [typeKey, setTypeKey] = useState(reportableTypes[0]?.key ?? '');
  const [severityKey, setSeverityKey] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault(); if (!title.trim()) return;
    setBusy(true);
    try {
      const issue = await appRest<Issue>('POST', '/issues', {
        typeKey, title: title.trim(),
        description: description.trim() || undefined,
        severityKey: severityKey || undefined,
      });
      onCreated(issue.id);
    } catch (e) { onError(fmt(e)); setBusy(false); }
  };

  return (
    <div className="card mb-3">
      <div className="card-header"><strong>Report a problem</strong></div>
      <div className="card-body">
        <form onSubmit={submit}>
          <div className="mb-2">
            <label className="form-label small mb-0">What kind of problem?</label>
            <select className="form-select" value={typeKey} onChange={(e) => setTypeKey(e.target.value)}>
              {reportableTypes.map((t) => <option key={t.id} value={t.key}>{t.name}</option>)}
            </select>
          </div>
          <div className="mb-2">
            <label className="form-label small mb-0">How urgent is it?</label>
            <select className="form-select" value={severityKey} onChange={(e) => setSeverityKey(e.target.value)}>
              <option value="">Let AquaStar decide</option>
              {meta.severities.map((s) => <option key={s.id} value={s.key}>{s.name}</option>)}
            </select>
          </div>
          <div className="mb-2">
            <label className="form-label small mb-0">Summary</label>
            <input className="form-control" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Pump won’t turn on" required />
          </div>
          <div className="mb-3">
            <label className="form-label small mb-0">Details</label>
            <textarea className="form-control" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Tell us what’s happening, when it started, anything you’ve tried…" />
          </div>
          <div className="d-flex gap-2">
            <button type="submit" className="btn btn-primary" disabled={busy || !title.trim()}>{busy ? 'Submitting…' : 'Submit'}</button>
            <button type="button" className="btn btn-outline-secondary" onClick={onCancel}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── detail ─────────────────────────────────────────────────────────────

function SupportDetail({ issueId }: { issueId: string }) {
  const navigate = useNavigate();
  const [meta, setMeta] = useState<Meta | null>(null);
  const [detail, setDetail] = useState<DetailResp | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [reply, setReply] = useState('');

  const load = useCallback(async () => {
    setErr(null);
    try {
      const [m, d] = await Promise.all([
        appRest<Meta>('GET', '/issues/meta'),
        appRest<DetailResp>('GET', `/issues/${issueId}`),
      ]);
      setMeta(m); setDetail(d);
    } catch (e) { setErr(fmt(e)); }
  }, [issueId]);
  useEffect(() => { void load(); }, [load]);

  const status = useMemo(() => meta?.statuses.find((s) => s.id === detail?.issue.statusId), [meta, detail]);

  const send = async (e: FormEvent) => {
    e.preventDefault(); if (!reply.trim()) return;
    try {
      // Clients can only post public comments — the backend enforces it too.
      await appRest('POST', `/issues/${issueId}/comments`, { body: reply.trim(), visibility: 'public' });
      setReply(''); await load();
    } catch (e) { setErr(fmt(e)); }
  };

  return (
    <>
      <AppHeader />
      <div className="container py-4" style={{ maxWidth: 760 }}>
        <button className="btn btn-link p-0 mb-3" onClick={() => navigate('/support')}>← Back to support</button>
        {err ? <div className="alert alert-warning">{err}</div> : null}
        {!detail ? <div className="text-muted">Loading…</div> : (
          <>
            <div className="d-flex align-items-center justify-content-between">
              <h3 className="mb-0">{detail.issue.title}</h3>
              <StatusBadge name={status?.name} cat={status?.statusCategory} />
            </div>
            <div className="text-muted small mb-3" style={{ fontFamily: 'monospace' }}>{detail.issue.key}</div>
            {detail.issue.description ? <p>{detail.issue.description}</p> : null}

            <h6 className="text-muted text-uppercase small mt-4">Updates</h6>
            {detail.comments.length === 0 ? (
              <div className="text-muted small">No updates yet. We’ll post here as we work on it.</div>
            ) : detail.comments.map((c) => (
              <div key={c.id} className="border-start ps-3 py-2 mb-2" style={{ borderColor: 'var(--aqua-border)' }}>
                <div className="small">{c.body}</div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>{c.authorKind === 'client' ? 'You' : 'AquaStar'} · {rel(c.createdAt)}</div>
              </div>
            ))}

            <form className="mt-3" onSubmit={send}>
              <textarea className="form-control mb-2" rows={3} placeholder="Add a reply…" value={reply} onChange={(e) => setReply(e.target.value)} />
              <button className="btn btn-primary" disabled={!reply.trim()}>Send reply</button>
            </form>
          </>
        )}
      </div>
    </>
  );
}

// ── helpers ─────────────────────────────────────────────────────────────

function StatusBadge({ name, cat }: { name?: string; cat?: string }) {
  const cls = cat === 'resolved' || cat === 'closed' ? 'bg-success'
    : cat === 'in_progress' ? 'bg-primary'
    : cat === 'blocked' ? 'bg-warning text-dark'
    : 'bg-secondary';
  return <span className={`badge ${cls}`}>{name ?? cat ?? 'Open'}</span>;
}
function fmt(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return String(e);
}
function rel(iso: string): string {
  const sec = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  const m = Math.round(sec / 60); if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60); if (h < 24) return `${h}h ago`;
  return new Date(iso).toLocaleDateString();
}
